/**
 * Billing Management Page
 * Manages billing cycles, invoices, and subscriptions for organizations
 */

import { useState, useEffect } from 'react'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import { useMasterAdmin } from '../contexts/MasterAdminContext'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Select from '../components/Select'
import {
  DollarSign,
  FileText,
  Calendar,
  Download,
  Plus,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Eye,
  Send,
  Shield,
  ExternalLink,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  getBillingCycles,
  getBillingSummary,
  updateBillingCycleStatus,
} from '../services/billingService'
import {
  getInvoices,
  getInvoice,
  generateInvoiceFromBillingCycle,
  markInvoiceAsSent,
  markInvoiceAsPaid,
  exportInvoices,
  getOverdueInvoices,
} from '../services/invoiceService'
import { getSubscription } from '../services/organization'
import { getBillingCycleInfo, updateBillingCycle } from '../services/billingCycleService'
import { formatDate } from '../utils/helpers'

const BillingManagement = () => {
  const organizationId = useOrganizationId()
  const { organization, isAdmin } = useOrganization()
  const { isMasterAdmin } = useMasterAdmin()
  const [activeTab, setActiveTab] = useState('subscription')
  const [billingSummary, setBillingSummary] = useState(null)
  const [billingCycles, setBillingCycles] = useState([])
  const [invoices, setInvoices] = useState([])
  const [overdueInvoices, setOverdueInvoices] = useState([])
  const [subscription, setSubscription] = useState(null)
  const [billingCycleInfo, setBillingCycleInfo] = useState(null)
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly')
  const [updatingCycle, setUpdatingCycle] = useState(false)
  const [loading, setLoading] = useState(true)
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [filter, setFilter] = useState('all')

  useEffect(() => {
    if (organizationId) {
      loadData()
    }
  }, [organizationId, filter])

  const loadData = async () => {
    setLoading(true)
    try {
      const [summary, cycles, invoicesData, overdue, sub, cycleInfo] = await Promise.all([
        getBillingSummary(organizationId),
        getBillingCycles(organizationId, filter !== 'all' ? { status: filter } : {}),
        getInvoices(organizationId, filter !== 'all' ? { status: filter } : {}),
        getOverdueInvoices(organizationId),
        getSubscription(organizationId),
        getBillingCycleInfo(organizationId),
      ])

      if (summary.success) {
        setBillingSummary(summary.data)
      }
      if (cycles.success) {
        setBillingCycles(cycles.data)
      }
      if (invoicesData.success) {
        setInvoices(invoicesData.data)
      }
      if (overdue.success) {
        setOverdueInvoices(overdue.data)
      }
      if (sub) {
        setSubscription(sub)
      }
      if (cycleInfo.success && cycleInfo.data) {
        setBillingCycleInfo(cycleInfo.data)
        setSelectedBillingCycle(cycleInfo.data.currentCycle || 'monthly')
      }
    } catch (error) {
      console.error('Error loading billing data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateBillingCycle = async () => {
    if (!organizationId) return
    try {
      setUpdatingCycle(true)
      const result = await updateBillingCycle(organizationId, selectedBillingCycle)
      if (result.success) {
        await loadData()
        alert(`Billing cycle updated to ${selectedBillingCycle}. Changes will take effect at the end of the current billing period.`)
      } else {
        throw new Error(result.error || 'Failed to update billing cycle')
      }
    } catch (error) {
      console.error('Error updating billing cycle:', error)
      alert(error.message || 'Failed to update billing cycle')
    } finally {
      setUpdatingCycle(false)
    }
  }

  const handleViewInvoice = async (invoiceId) => {
    const result = await getInvoice(invoiceId, organizationId)
    if (result.success) {
      setSelectedInvoice(result.data)
      setShowInvoiceModal(true)
    }
  }

  const handleGenerateInvoice = async (billingCycleId) => {
    if (!confirm('Generate invoice for this billing cycle?')) return

    const result = await generateInvoiceFromBillingCycle(organizationId, billingCycleId)
    if (result.success) {
      alert('Invoice generated successfully')
      loadData()
    } else {
      alert(`Error: ${result.error}`)
    }
  }

  const handleMarkAsSent = async (invoiceId) => {
    const result = await markInvoiceAsSent(invoiceId, organizationId)
    if (result.success) {
      loadData()
    } else {
      alert(`Error: ${result.error}`)
    }
  }

  const handleMarkAsPaid = async (invoiceId) => {
    const result = await markInvoiceAsPaid(invoiceId, organizationId)
    if (result.success) {
      loadData()
    } else {
      alert(`Error: ${result.error}`)
    }
  }

  const handleExport = async (format) => {
    await exportInvoices(invoices, format)
  }

  if (loading) {
    return (
      <div className="section-container">
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading billing information...</p>
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'subscription', label: 'Subscription', icon: DollarSign },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    // Billing cycles only relevant for master admin
    ...(isMasterAdmin ? [{ id: 'cycles', label: 'Billing Cycles', icon: Calendar }] : []),
  ]

  return (
    <div className="section-container space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2 mb-2">
            <DollarSign className="w-8 h-8" />
            Billing & Invoices
          </h1>
          <p className="text-gray-600">
            {isMasterAdmin
              ? 'Manage billing, invoices, and revenue for your organization.'
              : 'View your subscription status and invoices for NaturAssist.'}
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/legal/terms"
            className="text-sm text-blue-600 hover:underline flex items-center gap-1 px-3 py-2"
          >
            <FileText className="w-4 h-4" />
            Terms
          </Link>
          <Link
            to="/legal/privacy"
            className="text-sm text-blue-600 hover:underline flex items-center gap-1 px-3 py-2"
          >
            <Shield className="w-4 h-4" />
            Privacy
          </Link>
        </div>
        <div className="flex gap-2">
          {activeTab === 'invoices' && invoices.length > 0 && isMasterAdmin && (
            <>
              <Button variant="outline" onClick={() => handleExport('csv')}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button variant="outline" onClick={() => handleExport('xlsx')}>
                <Download className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
            </>
          )}
          <Button onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Billing Summary - master admin vs regular users */}
      {billingSummary && isMasterAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-6">
            <p className="text-sm text-gray-600 mb-2">Total Revenue</p>
            <p className="text-2xl font-bold">
              ${billingSummary.total_revenue?.toLocaleString() || 0}
            </p>
          </Card>
          <Card className="p-6">
            <p className="text-sm text-gray-600 mb-2">Pending Amount</p>
            <p className="text-2xl font-bold text-yellow-600">
              ${billingSummary.pending_amount?.toLocaleString() || 0}
            </p>
          </Card>
          <Card className="p-6">
            <p className="text-sm text-gray-600 mb-2">Overdue Amount</p>
            <p className="text-2xl font-bold text-red-600">
              ${billingSummary.overdue_amount?.toLocaleString() || 0}
            </p>
          </Card>
          <Card className="p-6">
            <p className="text-sm text-gray-600 mb-2">Next Billing</p>
            <p className="text-2xl font-bold">
              {billingSummary.next_billing_date
                ? formatDate(billingSummary.next_billing_date)
                : 'N/A'}
            </p>
          </Card>
        </div>
      )}

      {billingSummary && !isMasterAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card className="p-6">
            <p className="text-sm text-gray-600 mb-2">Next Billing Date</p>
            <p className="text-2xl font-bold">
              {billingSummary.next_billing_date
                ? formatDate(billingSummary.next_billing_date)
                : 'N/A'}
            </p>
          </Card>
          <Card className="p-6">
            <p className="text-sm text-gray-600 mb-2">Outstanding Balance</p>
            <p
              className={`text-2xl font-bold ${
                (billingSummary.pending_amount || 0) + (billingSummary.overdue_amount || 0) > 0
                  ? 'text-yellow-600'
                  : 'text-green-600'
              }`}
            >
              $
              {(
                (billingSummary.pending_amount || 0) +
                (billingSummary.overdue_amount || 0)
              ).toLocaleString()}
            </p>
          </Card>
        </div>
      )}

      {/* Overdue Invoices Alert */}
      {overdueInvoices.length > 0 && (
        <Card className="bg-red-50 border-red-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-red-800 mb-2">Overdue Invoices</h3>
              <p className="text-sm text-red-600">
                {overdueInvoices.length} invoice(s) are overdue. Please review and make payment.
              </p>
            </div>
            <Button variant="outline" onClick={() => setActiveTab('invoices')}>
              View Invoices
            </Button>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 flex items-center space-x-2 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600 font-semibold'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'invoices' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold mb-4">Invoices</h2>
            {/* Filter dropdown only for master admin */}
            {isMasterAdmin && (
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg"
              >
                <option value="all">All</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
              </select>
            )}
          </div>

          {invoices.length === 0 ? (
            <Card className="p-8">
              <p className="text-gray-500 text-center">No invoices found</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {invoices.map((invoice) => (
                <Card key={invoice.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-2">
                        <p className="font-mono font-semibold text-lg">{invoice.invoice_number}</p>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            invoice.status === 'paid'
                              ? 'bg-green-100 text-green-800'
                              : invoice.status === 'overdue'
                              ? 'bg-red-100 text-red-800'
                              : invoice.status === 'sent'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {invoice.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        Invoice Date: {formatDate(invoice.invoice_date)}
                      </p>
                      <p className="text-sm text-gray-600 mb-2">
                        Due Date: {formatDate(invoice.due_date)}
                      </p>
                      <p className="text-2xl font-bold mt-2">
                        ${invoice.total_amount.toLocaleString()} {invoice.currency}
                      </p>
                      {invoice.notes && (
                        <p className="text-sm text-gray-600 mt-3 p-3 bg-gray-50 rounded-lg">
                          {invoice.notes}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewInvoice(invoice.id)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      {isAdmin && invoice.status === 'draft' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMarkAsSent(invoice.id)}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Send
                        </Button>
                      )}
                      {isAdmin && invoice.status !== 'paid' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMarkAsPaid(invoice.id)}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Mark Paid
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'cycles' && isMasterAdmin && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold mb-4">Billing Cycles</h2>
          {billingCycles.length === 0 ? (
            <Card className="p-8">
              <p className="text-gray-500 text-center">No billing cycles found</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {billingCycles.map((cycle) => (
                <Card key={cycle.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-lg mb-2">
                        {formatDate(cycle.cycle_start_date)} - {formatDate(cycle.cycle_end_date)}
                      </p>
                      <p className="text-sm text-gray-600 mb-2">
                        Type: {cycle.billing_cycle_type}
                      </p>
                      <p className="text-sm text-gray-600 mb-2">
                        Due: {formatDate(cycle.due_date)}
                      </p>
                      <p className="text-2xl font-bold mt-3">
                        ${cycle.amount.toLocaleString()} {cycle.currency}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          cycle.status === 'paid'
                            ? 'bg-green-100 text-green-800'
                            : cycle.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {cycle.status}
                      </span>
                      {isAdmin && cycle.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleGenerateInvoice(cycle.id)}
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Generate Invoice
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'subscription' && (
        <div className="space-y-6">
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-6">Current Subscription</h2>
            {subscription ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-600">Subscription Tier</span>
                  <span className="font-semibold text-lg">
                    {(() => {
                      const tier = subscription.tier || organization?.subscription_tier || 'demo'
                      const tierLabels = {
                        demo: 'Demo',
                        lite: 'Lite',
                        essential: 'Essential',
                        professional: 'Professional',
                        growth: 'Enterprise',
                        white_label: 'White Label',
                        enterprise: 'Enterprise',
                      }
                      return tierLabels[tier] || tier
                    })()}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-gray-600">Status</span>
                  <span
                    className={`font-semibold capitalize px-3 py-1 rounded-full text-sm ${
                      (subscription.status || 'active') === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {subscription.status || 'active'}
                  </span>
                </div>
                {billingCycleInfo && (
                  <>
                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                      <span className="text-gray-600 flex items-center gap-2">
                        <Calendar size={16} />
                        Billing Cycle
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        billingCycleInfo.currentCycle === 'yearly'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {billingCycleInfo.currentCycle === 'yearly' ? 'Yearly' : 'Monthly'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                      <span className="text-gray-600">Current Period</span>
                      <span className="font-semibold">
                        {formatDate(billingCycleInfo.currentPeriodStart)} - {formatDate(billingCycleInfo.currentPeriodEnd)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                      <span className="text-gray-600 flex items-center gap-2">
                        <Calendar size={16} />
                        Next Renewal Date
                      </span>
                      <span className="font-semibold text-primary-600">
                        {formatDate(billingCycleInfo.nextRenewalDate)}
                      </span>
                    </div>
                  </>
                )}
                {subscription.starts_at && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Start Date</span>
                    <span className="font-semibold">{formatDate(subscription.starts_at)}</span>
                  </div>
                )}
                {subscription.ends_at && !billingCycleInfo && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">End Date</span>
                    <span className="font-semibold">{formatDate(subscription.ends_at)}</span>
                  </div>
                )}
                {organization?.trial_ends_at && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <span className="text-gray-600">Trial Ends</span>
                    <span className="font-semibold">{formatDate(organization.trial_ends_at)}</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No subscription information available</p>
            )}
          </Card>

          {/* Billing Cycle Management - Only for admins */}
          {isAdmin && billingCycleInfo && (
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-6">Manage Billing Cycle</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Select Billing Cycle
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setSelectedBillingCycle('monthly')}
                      className={`flex-1 px-4 py-4 rounded-lg border-2 transition-colors ${
                        selectedBillingCycle === 'monthly'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-lg">Monthly</div>
                      <div className="text-sm mt-1 text-gray-600">
                        R{billingCycleInfo.monthlyPrice?.toLocaleString() || '0'}/month
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedBillingCycle('yearly')}
                      className={`flex-1 px-4 py-4 rounded-lg border-2 transition-colors ${
                        selectedBillingCycle === 'yearly'
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-lg">Yearly</div>
                      <div className="text-sm mt-1 text-gray-600">
                        R{billingCycleInfo.yearlyPrice?.toLocaleString() || '0'}/year
                      </div>
                      {billingCycleInfo.yearlyPrice && billingCycleInfo.monthlyPrice && (
                        <div className="text-xs text-green-600 mt-1 font-semibold">
                          Save {Math.round((1 - (billingCycleInfo.yearlyPrice / 12) / billingCycleInfo.monthlyPrice) * 100)}%
                        </div>
                      )}
                    </button>
                  </div>
                </div>

                {selectedBillingCycle !== billingCycleInfo.currentCycle && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800 font-semibold mb-1">
                      Change will take effect: {formatDate(billingCycleInfo.currentPeriodEnd)}
                    </p>
                    <p className="text-xs text-blue-600">
                      Your billing cycle will change to {selectedBillingCycle} at the end of your current period. Your next renewal date will be updated accordingly.
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleUpdateBillingCycle}
                  disabled={updatingCycle || selectedBillingCycle === billingCycleInfo.currentCycle}
                  className="w-full"
                >
                  {updatingCycle ? 'Updating...' : 'Update Billing Cycle'}
                </Button>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Invoice Detail Modal */}
      {showInvoiceModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Invoice {selectedInvoice.invoice_number}</h2>
              <Button variant="ghost" onClick={() => setShowInvoiceModal(false)}>Close</Button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Invoice Date</p>
                  <p className="font-semibold">{formatDate(selectedInvoice.invoice_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Due Date</p>
                  <p className="font-semibold">{formatDate(selectedInvoice.due_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <span
                    className={`px-2 py-1 rounded text-xs ${
                      selectedInvoice.status === 'paid'
                        ? 'bg-green-100 text-green-800'
                        : selectedInvoice.status === 'overdue'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {selectedInvoice.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Payment Terms</p>
                  <p className="font-semibold">{selectedInvoice.payment_terms}</p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Line Items</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Description</th>
                      <th className="text-right p-2">Quantity</th>
                      <th className="text-right p-2">Unit Price</th>
                      <th className="text-right p-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedInvoice.line_items || []).map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-2">{item.description}</td>
                        <td className="text-right p-2">{item.quantity || 1}</td>
                        <td className="text-right p-2">${item.unit_price?.toLocaleString()}</td>
                        <td className="text-right p-2">${item.amount?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3" className="text-right p-2 font-semibold">
                        Subtotal:
                      </td>
                      <td className="text-right p-2 font-semibold">
                        ${selectedInvoice.subtotal.toLocaleString()}
                      </td>
                    </tr>
                    {selectedInvoice.tax_amount > 0 && (
                      <tr>
                        <td colSpan="3" className="text-right p-2">Tax:</td>
                        <td className="text-right p-2">${selectedInvoice.tax_amount.toLocaleString()}</td>
                      </tr>
                    )}
                    {selectedInvoice.discount_amount > 0 && (
                      <tr>
                        <td colSpan="3" className="text-right p-2">Discount:</td>
                        <td className="text-right p-2">
                          -${selectedInvoice.discount_amount.toLocaleString()}
                        </td>
                      </tr>
                    )}
                    <tr className="border-t-2">
                      <td colSpan="3" className="text-right p-2 font-bold text-lg">Total:</td>
                      <td className="text-right p-2 font-bold text-lg">
                        ${selectedInvoice.total_amount.toLocaleString()} {selectedInvoice.currency}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {selectedInvoice.notes && (
                <div>
                  <h3 className="font-semibold mb-2">Notes</h3>
                  <p className="text-sm text-gray-600">{selectedInvoice.notes}</p>
                </div>
              )}

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => handleExport('csv')}>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button variant="outline" onClick={() => handleExport('xlsx')}>
                  <Download className="w-4 h-4 mr-2" />
                  Export Excel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

export default BillingManagement

